package com.steamstreet.vegasful.web

/**
 * Which app store the install bar should offer a given user agent, or null if it should offer
 * nothing at all.
 *
 * Pure string work, and in `commonMain` rather than next to the client module that calls it, so
 * that it can be tested on the JVM. The rule it encodes is the one everything else about the
 * install bar rests on, and it is invisible when wrong: getting it backwards shows iOS Safari two
 * competing offers, or shows Android nothing, and neither looks like a failure from the outside.
 *
 * The three outcomes:
 *
 * - **Desktop gets nothing.** The footer store links are already there, and a bar offering a phone
 *   app to somebody on a laptop is an interruption with no action behind it.
 * - **iOS Safari gets nothing.** `base.kt` emits an `apple-itunes-app` Smart App Banner, which
 *   Safari draws *outside* the document, so it costs no layout — and which reads "Open" and
 *   deep-links into the event for somebody who already has the app. Our bar can do neither. Two
 *   offers on one screen is what this whole change exists to stop.
 * - **Everything else on a phone gets the bar**, including the other iOS browsers, because none of
 *   them renders Apple's banner.
 *
 * See `specs/app-install-prompts.md`.
 */
fun installBarPlatform(userAgent: String): String? {
    val agent = userAgent.lowercase()
    val isIOS = agent.contains("iphone") || agent.contains("ipad") || agent.contains("ipod")

    if (isIOS) return if (isIOSSafari(agent)) null else "ios"

    return if (agent.contains("android")) "android" else null
}

/**
 * Whether an already-lowercased iOS user agent is Safari.
 *
 * Every other iOS browser is obliged to use the same WebKit engine, so they all still say
 * "Version/… Safari/…" and cannot be told apart by anything positive. They each *add* a token,
 * which is the only thing that distinguishes them — so this identifies Safari by the absence of
 * all of them rather than by its own name.
 *
 * The consequence of the "unknown means Safari" default is the safe direction: an iOS browser we
 * have not heard of is assumed to render the native banner, and is offered no bar. Worst case it
 * sees one offer instead of one offer; the other way round it sees two.
 */
private fun isIOSSafari(agent: String): Boolean =
    !agent.contains("crios") &&      // Chrome
            !agent.contains("fxios") &&      // Firefox
            !agent.contains("edgios") &&     // Edge
            !agent.contains("opt/")          // Opera Touch
